/**
 * ShowroomSelectionPopup Component
 *
 * A popup that appears when user hasn't selected a showroom yet.
 * Requests location permission and shows nearest showroom if available.
 *
 * Flow:
 * 1. Check if user has selected showroom (cookie) → If yes, don't show popup
 * 2. Check if popup was dismissed this session (Close / CTA) → If yes, don't show popup
 * 3. Request geolocation permission
 * 4. If granted, search for nearest showroom and display it
 * 5. User can confirm (set as My Showroom) or find another
 * 6. Auto-hide after 15s without session dismiss — refresh shows the popup again
 */

const AUTO_HIDE_MS = 15000;

import React, {useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {FormattedMessage, useIntl} from 'react-intl';

import {useSearchStores} from '@salesforce/commerce-sdk-react';
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  Skeleton,
  Spinner,
  Text,
  useMultiStyleConfig,
  VStack,
} from '@salesforce/retail-react-app/app/components/shared/ui';

/**
 * Custom close icon matching design specs
 * 16x16px, white stroke, opacity 0.7
 */
const PopupCloseIcon = (props) => (
  <Icon viewBox="0 0 16 16" {...props}>
    <g opacity="0.7">
      <path d="M0.5 15.5L15.5 0.5" stroke="white" strokeLinecap="round" />
      <path d="M15.5 15.5L0.5 0.5" stroke="white" strokeLinecap="round" />
    </g>
  </Icon>
);

import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation';
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer';

import {
  getSiteConfig,
  MAX_STORE_SEARCH_RADIUS,
  SHOWROOMS_LISTING_PATH,
} from '@amblique/king-living/app/constants/site-config';
import {useHeaderContext} from '@amblique/king-living/app/contexts/header-context';
import {
  getMyShowroomFromCookie,
  getStoreSlug,
  isShowroomPopupDismissed,
  parseAccountShowroom,
  setMyShowroomToCookie,
  setShowroomPopupDismissed,
  SHOWROOM_CHANGED_EVENT,
} from '@amblique/king-living/app/utils/utils';
import {useCartFlyoutContext} from '@amblique/king-living/app/contexts/cart-flyout-context';
import {usePdpConfigOverlayOpen} from '@amblique/king-living/app/hooks/use-pdp-config-overlay-open';
import {useEscapeToClose} from '@amblique/king-living/app/hooks/use-escape-to-close';
import {useShopTheLookDrawerOpen} from '@amblique/king-living/app/hooks/use-shop-the-look-drawer-open';

/** Stable id for aria-controls + dialog landmark (useId is not valid in HTML id attrs). */
export const SHOWROOM_POPUP_DIALOG_ID = 'showroom-selection-popup-dialog';

// Location states
const LOCATION_STATE = {
  IDLE: 'idle',
  REQUESTING: 'requesting',
  GRANTED: 'granted',
  DENIED: 'denied',
  UNAVAILABLE: 'unavailable',
};

/**
 * ShowroomSelectionPopup - Detects location and shows nearest showroom
 */
const ShowroomSelectionPopup = () => {
  const intl = useIntl();
  const navigate = useNavigation();
  const {isOpen: isCartFlyoutOpen} = useCartFlyoutContext();
  const isShopTheLookDrawerOpen = useShopTheLookDrawerOpen();
  const isPdpConfigOverlayOpen = usePdpConfigOverlayOpen();
  const {site, buildUrl} = useMultiSite();
  const {data: customer, isLoading: isCustomerLoading} = useCurrentCustomer();
  const {isRegistered} = customer || {};

  const siteConfig = getSiteConfig(site?.id);
  const defaultLocation = siteConfig?.defaultLocation;

  const [isVisible, setIsVisible] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [locationState, setLocationState] = useState(LOCATION_STATE.IDLE);
  const [userCoords, setUserCoords] = useState(null);
  const [searchCoords, setSearchCoords] = useState(null);
  const [detectedShowroom, setDetectedShowroom] = useState(null);
  const [searchCompleted, setSearchCompleted] = useState(false);
  const usedDefaultLocationFallbackRef = useRef(false);

  // Dynamic position state for popup
  const [popupPosition, setPopupPosition] = useState({
    top: 70,
    left: 20,
    arrowLeft: 155,
    isMobile: false,
  });
  const [hiddenByScroll, setHiddenByScroll] = useState(false);
  const lastScrollYRef = useRef(0);
  const popupRef = useRef(null);
  const closeButtonRef = useRef(null);
  const lastActionRef = useRef(null);
  const dialogTitleId = useId();

  const styles = useMultiStyleConfig('ShowroomSelectionPopup');
  const {isMegaMenuOpen, isSearchOpen} = useHeaderContext();

  // Desktop panel width; mobile uses viewport insets + MOBILE_POPUP_MAX_WIDTH
  const POPUP_WIDTH = 310;
  const POPUP_VIEWPORT_MARGIN = 20;
  const POPUP_GAP_BELOW_TRIGGER = 8;
  const MOBILE_POPUP_LEFT = 33;
  const MOBILE_POPUP_RIGHT = 32;
  const MOBILE_POPUP_MAX_WIDTH = 500;
  /** Phone only — inset left/right layout. Tablet+ anchors under the locale icon. */
  const PHONE_LAYOUT_MAX_WIDTH = 767;
  /** Below `lg`: header shows icon only (label hidden). */
  const COMPACT_TRIGGER_MAX_WIDTH = 991;
  const ARROW_HALF_WIDTH = 6;

  const getShowroomTargetCenterX = useCallback((showroomButton, useIconAnchor) => {
    // Desktop (`lg+`): prefer the visible text label center.
    // Tablet/phone: label is hidden — anchor to the location icon.
    if (!useIconAnchor) {
      const label = showroomButton.querySelector('[data-showroom-trigger-label]');

      if (label) {
        const labelRect = label.getBoundingClientRect();

        if (labelRect.width > 0) {
          return labelRect.left + (labelRect.width / 2);
        }
      }
    }

    const icon = showroomButton.querySelector('[data-showroom-trigger-icon], svg');

    if (icon) {
      const iconRect = icon.getBoundingClientRect();
      return iconRect.left + (iconRect.width / 2);
    }

    const buttonRect = showroomButton.getBoundingClientRect();
    return buttonRect.left + (buttonRect.width / 2);
  }, []);

  // Search for nearest store with max radius so a result is always returned when stores exist
  const {
    data: storesData,
    isLoading: isSearching,
    isError: isSearchError,
  } = useSearchStores(
    {
      parameters: {
        latitude: searchCoords?.latitude,
        longitude: searchCoords?.longitude,
        maxDistance: MAX_STORE_SEARCH_RADIUS,
        distanceUnit: siteConfig?.distanceUnit || 'km',
        limit: 1,
        locale: siteConfig?.defaultLocale || 'en-AU',
      },
    },
    {
      enabled: !!searchCoords?.latitude && searchCoords?.longitude != null && !searchCompleted,
    }
  );

  const isCloseOnlyFocusable = useMemo(
    () =>
      locationState === LOCATION_STATE.REQUESTING ||
      (locationState === LOCATION_STATE.GRANTED && isSearching && !searchCompleted),
    [locationState, isSearching, searchCompleted]
  );

  // Update detected showroom when store data arrives; if 0 stores, fallback to defaultLocation once
  useEffect(() => {
    if (isSearchError) {
      setSearchCompleted(true);

      return;
    }

    if (storesData?.data?.[0]) {
      const store = storesData.data[0];

      setDetectedShowroom({
        id: store.id,
        name: store.name,
        slug: getStoreSlug(store),
        city: store.city,
        stateCode: store.stateCode,
        postalCode: store.postalCode,
        distance: store.distance,
        lat: store.latitude,
        lng: store.longitude,
        c_slug: store.c_slug,
      });
      setSearchCompleted(true);
    } else if (storesData && !isSearching) {
      if (
        defaultLocation?.lat != null &&
        defaultLocation?.lng != null &&
        !usedDefaultLocationFallbackRef.current
      ) {
        usedDefaultLocationFallbackRef.current = true;
        setSearchCompleted(false);
        setSearchCoords({
          latitude: defaultLocation.lat,
          longitude: defaultLocation.lng,
        });
      } else {
        setSearchCompleted(true);
      }
    }
  }, [storesData, isSearching, isSearchError, defaultLocation]);

  // Request geolocation
  const requestLocation = useCallback(() => {
    usedDefaultLocationFallbackRef.current = false;
    setSearchCoords(null);
    setSearchCompleted(false);
    setDetectedShowroom(null);

    if (!navigator.geolocation) {
      setLocationState(LOCATION_STATE.UNAVAILABLE);

      return;
    }

    setLocationState(LOCATION_STATE.REQUESTING);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
        setUserCoords(coords);
        setSearchCoords(coords);
        setLocationState(LOCATION_STATE.GRANTED);
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          setLocationState(LOCATION_STATE.DENIED);
        } else {
          setLocationState(LOCATION_STATE.UNAVAILABLE);
        }
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000, // 5 minutes
      }
    );
  }, []);

  // Check if popup should be shown
  useEffect(() => {
    setIsMounted(true);

    if (typeof window === 'undefined' || !site?.id) return;

    // Check showroom from cookie
    const cookieShowroom = getMyShowroomFromCookie(site.id);

    // Check showroom from account (if user is logged in and data is loaded)
    const accountShowroom = (isRegistered && !isCustomerLoading && customer?.c_preferredStoreId) ?
      parseAccountShowroom(customer.c_preferredStoreId, site.id) :
      null;

    // Has showroom from either cookie or account
    const hasShowroom = cookieShowroom || accountShowroom;
    const isDismissed = isShowroomPopupDismissed(site.id);

    // If user has showroom (from cookie or account), close popup immediately
    if (hasShowroom) {
      setIsVisible(false);

      return;
    }

    // If no showroom and not dismissed, show popup
    if (!isDismissed) {
      // Show popup after delay
      const timer = setTimeout(() => {
        setIsVisible(true);
        // Auto-request location when popup shows
        requestLocation();
      }, 1000);

      return () => clearTimeout(timer);
    }

    // Listen for showroom changes
    const handleShowroomChanged = (event) => {
      if (event.detail?.showroom) {
        setIsVisible(false);
      }
    };

    window.addEventListener(SHOWROOM_CHANGED_EVENT, handleShowroomChanged);

    return () => {
      window.removeEventListener(SHOWROOM_CHANGED_EVENT, handleShowroomChanged);
    };
  }, [site?.id, isRegistered, isCustomerLoading, customer?.c_preferredStoreId, requestLocation]);

  // Persist dismiss for this browser session (Close / Escape / CTAs). Not used by auto-hide.
  const handleDismiss = useCallback(() => {
    setShowroomPopupDismissed(site?.id);
    setIsVisible(false);
  }, [site?.id]);

  // Soft-hide only — no sessionStorage, so a refresh can show the popup again.
  useEffect(() => {
    if (!isVisible) return undefined;

    const timer = setTimeout(() => {
      setIsVisible(false);
    }, AUTO_HIDE_MS);

    return () => clearTimeout(timer);
  }, [isVisible]);

  useEscapeToClose(isVisible && !hiddenByScroll, handleDismiss);

  const focusHeaderSearchTrigger = useCallback(() => {
    document.querySelector('[data-header-search-trigger]')?.focus();
  }, []);

  const focusShowroomTrigger = useCallback(() => {
    document.querySelector('[data-showroom-trigger]')?.focus();
  }, []);

  const focusLastPopupAction = useCallback(() => {
    if (isCloseOnlyFocusable) {
      closeButtonRef.current?.focus();

      return;
    }

    lastActionRef.current?.focus();
  }, [isCloseOnlyFocusable]);

  const handlePopupLastActionKeyDown = useCallback((event) => {
    if (event.key === 'Tab' && !event.shiftKey) {
      event.preventDefault();
      focusHeaderSearchTrigger();
    }
  }, [focusHeaderSearchTrigger]);

  const handleCloseButtonKeyDown = useCallback((event) => {
    if (event.key === 'Tab' && event.shiftKey) {
      event.preventDefault();
      focusShowroomTrigger();

      return;
    }

    if (event.key === 'Tab' && !event.shiftKey && isCloseOnlyFocusable) {
      event.preventDefault();
      focusHeaderSearchTrigger();
    }
  }, [focusHeaderSearchTrigger, focusShowroomTrigger, isCloseOnlyFocusable]);

  // Tab from showroom trigger → popup Close; sync trigger aria when tooltip is open.
  useLayoutEffect(() => {
    if (!isVisible || typeof document === 'undefined') {
      return undefined;
    }

    let trigger = null;
    let retryFrameId = null;

    const onTriggerKeyDown = (event) => {
      if (event.key !== 'Tab' || event.shiftKey || hiddenByScroll) {
        return;
      }

      event.preventDefault();
      closeButtonRef.current?.focus({preventScroll: true});
    };

    const attachTrigger = () => {
      trigger = document.querySelector('[data-showroom-trigger]');

      if (!trigger) {
        return false;
      }

      trigger.setAttribute('aria-expanded', 'true');
      trigger.setAttribute('aria-controls', SHOWROOM_POPUP_DIALOG_ID);
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.addEventListener('keydown', onTriggerKeyDown);

      return true;
    };

    if (!attachTrigger()) {
      retryFrameId = requestAnimationFrame(attachTrigger);
    }

    return () => {
      if (retryFrameId != null) {
        cancelAnimationFrame(retryFrameId);
      }

      trigger?.removeEventListener('keydown', onTriggerKeyDown);

      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
        trigger.removeAttribute('aria-controls');
        trigger.removeAttribute('aria-haspopup');
      }
    };
  }, [hiddenByScroll, isVisible]);

  useEffect(() => {
    if (!isVisible || typeof document === 'undefined') {
      return undefined;
    }

    const searchTrigger = document.querySelector('[data-header-search-trigger]');

    const onSearchKeyDown = (event) => {
      if (event.key === 'Tab' && event.shiftKey) {
        event.preventDefault();
        focusLastPopupAction();
      }
    };

    searchTrigger?.addEventListener('keydown', onSearchKeyDown);

    return () => {
      searchTrigger?.removeEventListener('keydown', onSearchKeyDown);
    };
  }, [focusLastPopupAction, isVisible]);

  // Handle confirm detected showroom
  const handleConfirmShowroom = () => {
    if (detectedShowroom) {
      setMyShowroomToCookie(site?.id, detectedShowroom);
    }
    setShowroomPopupDismissed(site?.id);
    setIsVisible(false);
  };

  // Handle find different showroom
  const handleFindShowroom = () => {
    setShowroomPopupDismissed(site?.id);
    setIsVisible(false);
    navigate(buildUrl(SHOWROOMS_LISTING_PATH));
  };

  // Center popup under showroom trigger; arrow tip points at trigger center.
  // Phone (< md): inset left/right. Tablet+ desktop: fixed width under locale icon/label.
  const updatePopupPosition = useCallback(() => {
    if (typeof window === 'undefined') return;

    const showroomButton = document.querySelector('[data-showroom-trigger]');

    if (!showroomButton) return;

    const rect = showroomButton.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const isPhoneLayout = viewportWidth <= PHONE_LAYOUT_MAX_WIDTH;
    const useIconAnchor = viewportWidth <= COMPACT_TRIGGER_MAX_WIDTH;
    const targetCenterX = getShowroomTargetCenterX(showroomButton, useIconAnchor);

    const idealLeft = targetCenterX - (POPUP_WIDTH / 2);
    const minLeft = POPUP_VIEWPORT_MARGIN;
    const maxLeft = viewportWidth - POPUP_WIDTH - POPUP_VIEWPORT_MARGIN;
    const centeredLeft = Math.max(minLeft, Math.min(idealLeft, maxLeft));
    const popupLeft = isPhoneLayout ? MOBILE_POPUP_LEFT : centeredLeft;
    const measuredPopupRect = popupRef.current?.getBoundingClientRect();
    const measuredPopupLeft = measuredPopupRect?.left ?? popupLeft;
    const measuredPopupWidth =
      measuredPopupRect?.width ??
      (isPhoneLayout ?
        Math.min(
          MOBILE_POPUP_MAX_WIDTH,
          viewportWidth - MOBILE_POPUP_LEFT - MOBILE_POPUP_RIGHT
        ) :
        POPUP_WIDTH);

    const arrowLeft = Math.max(
      ARROW_HALF_WIDTH,
      Math.min(
        targetCenterX - measuredPopupLeft,
        measuredPopupWidth - ARROW_HALF_WIDTH
      )
    );

    setPopupPosition({
      top: rect.bottom + POPUP_GAP_BELOW_TRIGGER,
      left: popupLeft,
      arrowLeft,
      isMobile: isPhoneLayout,
    });
  }, [getShowroomTargetCenterX]);

  // Update position when popup becomes visible or layout changes
  useEffect(() => {
    if (!isVisible) return;

    updatePopupPosition();
    const frameId = requestAnimationFrame(() => {
      updatePopupPosition();
    });

    window.addEventListener('resize', updatePopupPosition);
    window.addEventListener('scroll', updatePopupPosition, {passive: true});

    const observer = new MutationObserver(() => {
      setTimeout(updatePopupPosition, 100);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', updatePopupPosition);
      window.removeEventListener('scroll', updatePopupPosition);
      observer.disconnect();
    };
  }, [isVisible, updatePopupPosition]);

  /** Scroll down = hide; scroll up = show (keeps anchor position logic unchanged). */
  useEffect(() => {
    if (!isVisible) return;
    if (typeof window === 'undefined') return;

    setHiddenByScroll(false);
    lastScrollYRef.current = window.scrollY ?? document.documentElement.scrollTop ?? 0;

    const SCROLL_DELTA = 8;
    const onScroll = () => {
      const y = window.scrollY ?? document.documentElement.scrollTop;
      const prev = lastScrollYRef.current;
      if (y - prev > SCROLL_DELTA) {
        setHiddenByScroll(true);
      } else if (prev - y > SCROLL_DELTA) {
        setHiddenByScroll(false);
      }
      lastScrollYRef.current = y;
    };

    window.addEventListener('scroll', onScroll, {passive: true});
    return () => window.removeEventListener('scroll', onScroll);
  }, [isVisible]);

  // Don't render on server, when not visible, or when overlay navigation is open
  if (!isMounted || !isVisible || isMegaMenuOpen || isSearchOpen) {
    return null;
  }

  // Determine what to show
  const isLoading = isCloseOnlyFocusable;
  const hasDetectedShowroom = detectedShowroom && locationState === LOCATION_STATE.GRANTED;

  const popupUi = (
    <Box
      ref={popupRef}
      id={SHOWROOM_POPUP_DIALOG_ID}
      aria-labelledby={dialogTitleId}
      aria-modal="false"
      role="dialog"
      aria-hidden={hiddenByScroll}
      left={popupPosition.isMobile ? `${MOBILE_POPUP_LEFT}px` : `${popupPosition.left}px`}
      maxW={
        popupPosition.isMobile ?
          `${MOBILE_POPUP_MAX_WIDTH}px` :
          `${POPUP_WIDTH}px`
      }
      opacity={hiddenByScroll ? 0 : 1}
      pointerEvents={hiddenByScroll ? 'none' : 'auto'}
      position="fixed"
      right={popupPosition.isMobile ? `${MOBILE_POPUP_RIGHT}px` : undefined}
      top={`${popupPosition.top}px`}
      transition="opacity 0.2s ease"
      width={popupPosition.isMobile ? 'auto' : `${POPUP_WIDTH}px`}
      zIndex={
        isCartFlyoutOpen || isShopTheLookDrawerOpen ?
          'overlay' :
          isPdpConfigOverlayOpen ?
            -1 :
            'popover'
      }
    >
      {/* Arrow pointing up - aligned to showroom trigger center */}
      <Box
        {...styles.arrow}
        left={`${popupPosition.arrowLeft}px`}
      />

      <Box {...styles.panel}>
        {/* Close — existing dismiss path (sessionStorage); auto-hide does not call this */}
        <Box
          ref={closeButtonRef}
          as="button"
          type="button"
          {...styles.closeButton}
          aria-label={intl.formatMessage({
            defaultMessage: 'Close',
            id: 'showroom_popup.close',
          })}
          onClick={handleConfirmShowroom}
          onKeyDown={handleCloseButtonKeyDown}
        >
          <PopupCloseIcon boxSize="16px" />
        </Box>

        {/* Loading state */}
        {isLoading && (
          <VStack {...styles.contentStack}>
            <Heading id={dialogTitleId} {...styles.title} pr={8}>
              <FormattedMessage
                defaultMessage="Tailor your KING experience"
                id="showroom_popup.title"
              />
            </Heading>
            <Flex align="center" gap={2}>
              <Spinner color="white" size="sm" />
              <Text {...styles.loadingText}>
                <FormattedMessage
                  defaultMessage="Finding your nearest showroom..."
                  id="showroom_popup.loading"
                />
              </Text>
            </Flex>
            <Skeleton {...styles.buttonSkeleton} />
          </VStack>
        )}

        {/* Detected showroom */}
        {!isLoading && hasDetectedShowroom && (
          <VStack {...styles.contentStack}>
            <Box {...styles.textBlock}>
              <Heading id={dialogTitleId} {...styles.title}>
                <FormattedMessage
                  defaultMessage="Tailor your KING experience"
                  id="showroom_popup.title"
                />
              </Heading>
              <Text
                {...styles.showroomLink}
                href={buildUrl(`/showroom/${detectedShowroom.slug}`)}
                onClick={(e) => {
                  e.preventDefault();
                  handleDismiss();
                  navigate(buildUrl(`/showroom/${detectedShowroom.slug}`));
                }}
              >
                {detectedShowroom.name}{' '}
                <FormattedMessage
                  defaultMessage="showroom"
                  id="showroom_popup.showroom_suffix"
                />
                {(detectedShowroom.stateCode || detectedShowroom.postalCode) && (
                  <>
                    {', '}
                    {[detectedShowroom.stateCode, detectedShowroom.postalCode].filter(Boolean).join(' ')}
                  </>
                )}
              </Text>
              <Text {...styles.description}>
                <FormattedMessage
                  defaultMessage="We've found your nearest showroom for a more personalised experience."
                  id="showroom_popup.detected_description"
                />
              </Text>
            </Box>

            <HStack {...styles.buttonRow}>
              <Button {...styles.confirmButton} variant="outline" onClick={handleConfirmShowroom}>
                <FormattedMessage
                  defaultMessage="Confirm store"
                  id="showroom_popup.confirm"
                />
              </Button>

              <Button
                ref={lastActionRef}
                {...styles.changeButton}
                onClick={handleFindShowroom}
                onKeyDown={handlePopupLastActionKeyDown}
              >
                <FormattedMessage
                  defaultMessage="Change store"
                  id="showroom_popup.change"
                />
              </Button>
            </HStack>
          </VStack>
        )}

        {/* No showroom found or location failed */}
        {!isLoading && !hasDetectedShowroom && (
          <VStack {...styles.contentStack}>
            <Box {...styles.textBlock}>
              <Heading id={dialogTitleId} {...styles.title}>
                <FormattedMessage
                  defaultMessage="Tailor your KING experience"
                  id="showroom_popup.title"
                />
              </Heading>
              <Text {...styles.description}>
                <FormattedMessage
                  defaultMessage="Set your local KING showroom for a more personalised experience."
                  id="showroom_popup.description"
                />
              </Text>
            </Box>

            <Button
              ref={lastActionRef}
              {...styles.ctaButton}
              onClick={handleFindShowroom}
              onKeyDown={handlePopupLastActionKeyDown}
            >
              <FormattedMessage
                defaultMessage="Choose showroom"
                id="showroom_popup.cta"
              />
            </Button>
          </VStack>
        )}
      </Box>
    </Box>
  );

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(popupUi, document.body);
};

export default ShowroomSelectionPopup;
